import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import "./Invites.css";
import TabSelector from "../../components/tabSelector/TabSelector";
import EventSmallCard from "../../components/eventSmallCard/EventSmallCard";
import Header from "../../components/header/Header";
import { CalendarIcon, PlusCircleIcon } from "../../helper/icon";
import { AuthContext } from "../../context/auth";
import { useLocation, useNavigate } from "react-router-dom";
import {
  formatDateLessThanOneDay,
  formatDateWithOrdinal,
} from "../../helper/utils";
import AddEventModal from "../../Modals/addEventModal/AddEventModal";
import { getEventStatus } from "../../helper/utils";
import AcceptModal from "../../Modals/acceptModal/AcceptModal";
import DeclineModal from "../../Modals/declineModal/DeclineModal";
import PurchaseOptionsModal from "../../Modals/purchaseOptionsModal/PurchaseOptionsModal";
import MandatoryPurchaseModal from "../../Modals/mandatoryPurchaseModal/MandatoryPurchaseModal";
import { addGuestToEvent } from "../../helper/addGuestToEvent";
import { addUsherToEvent } from "../../helper/addUsherToEvent";

const Invites = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [pendingInvites, setPendingInvites] = useState([]);
  const [historyInvites, setHistoryInvites] = useState([]);
  const [events, setEvents] = useState({ upcomingEvents: [], pastEvents: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [isPurchaseOptionsModalOpen, setIsPurchaseOptionsModalOpen] =
    useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [isMandatoryPurchaseModalOpen, setIsMandatoryPurchaseModalOpen] =
    useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [purchaseType, setPurchaseType] = useState(null);
  const [purchaseOptions, setPurchaseOptions] = useState([]);
  const [showRoleBenefitsModal, setShowRoleBenefitsModal] = useState(false);
  const [newRole, setNewRole] = useState(null);

  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { inviteCode, hostId, reloadHome } = location.state || {};
  const reload = new URLSearchParams(location.search).get("reload") === "true";
  const storedUser = localStorage.getItem("inviteUser");
  const parsedStoredUser = storedUser ? JSON.parse(storedUser) : null;

  const userId =
    user?._id || location.state?.user?.userId || parsedStoredUser?.userId;
  const role = user?.activeRole || location.state?.user?.role;

  const params = new URLSearchParams(location.search);

  const inviteCodeFromState =
    location.state?.inviteCode ||
    params.get("inviteCode") ||
    params.get("invitecode");

  const hostIdFromState = location.state?.hostId || params.get("hostId");

  const isUsher = role === "usher";
  const isGuest = role === "guest";

  useEffect(() => {
    const attachUserToInvite = async () => {
      const inviteProcessed = sessionStorage.getItem("inviteProcessed");

      if (
        !inviteCodeFromState ||
        !hostIdFromState ||
        !userId ||
        !role ||
        inviteProcessed
      ) {
        return;
      }

      try {
        let response;

        if (role === "guest") {
          response = await addGuestToEvent(
            inviteCodeFromState,
            hostIdFromState,
            userId,
          );
        }

        if (role === "usher") {
          response = await addUsherToEvent(
            inviteCodeFromState,
            hostIdFromState,
            userId,
          );
        }

        if (response?.isWrongRoleCode) {
          console.warn("Invite code is for a different role");
          return;
        }

        if (response?.isEventDatePassed) {
          console.warn("Event date has already passed");
          return;
        }

        sessionStorage.setItem("inviteProcessed", "true");

        navigate("/invites?reload=true", { replace: true });
      } catch (err) {
        console.error("Failed to attach invite:", err);
      }
    };

    attachUserToInvite();
  }, [inviteCodeFromState, hostIdFromState, userId, role, navigate]);

  useEffect(() => {
    return () => {
      sessionStorage.removeItem("inviteProcessed");
    };
  }, []);

  useEffect(() => {
    const stored = sessionStorage.getItem("showRoleBenefits");

    if (stored) {
      const parsed = JSON.parse(stored);

      setNewRole(parsed.role);
      setShowRoleBenefitsModal(true);

      sessionStorage.removeItem("showRoleBenefits");
    }
  }, []);

  useEffect(() => {
    const loadInvites = async () => {
      if (!userId) {
        setError("Please log in to view invites.");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const data = await fetchInvites(userId, role);
        setPendingInvites(data.pendingInvites || []);
        setHistoryInvites(data.historyInvites || []);

        const allUpcoming = [
          ...(data.upcomingEvents || []),
          ...(data.activeEvents || []),
        ];

        setEvents({
          upcomingEvents: allUpcoming,
          pastEvents: data.pastEvents || [],
        });
      } catch (error) {
        setError("Failed to load your events. Please try again.", error);
      } finally {
        setLoading(false);
      }
    };

    loadInvites();
  }, [userId, reload, role, reloadHome, user?.activeRole]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    const dateUpdateInterval = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearInterval(dateUpdateInterval);
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && userId && role) {
        const data = await fetchInvites(userId, role);
        setPendingInvites(data.pendingInvites || []);
        setHistoryInvites(data.historyInvites || []);
        setEvents({
          upcomingEvents: [
            ...(data.upcomingEvents || []),
            ...(data.activeEvents || []),
          ],
          pastEvents: data.pastEvents || [],
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [userId, role]);

  const fetchInvites = async (
    userId,
    role,
    page = 1,
    perPage = 50,
    sort = "asc",
  ) => {
    if (!userId || !role) {
      return {
        pendingInvites: [],
        historyInvites: [],
        upcomingEvents: [],
        activeEvents: [],
        pastEvents: [],
      };
    }

    let url = "";
    if (role === "guest") {
      url = `/events/guest/${userId}/invite`;
    } else if (role === "usher") {
      url = `/events/usher/${userId}/invite`;
    } else {
      url = `/events/host/${userId}`;
    }

    try {
      const response = await axios.get(url, {
        params: { page, perPage, sort, filter: "all" },
      });

      if (!response.data.successStatus) {
        setError(response.data.message || "Failed to load invites.");
        return {
          pendingInvites: [],
          historyInvites: [],
          upcomingEvents: [],
          activeEvents: [],
          pastEvents: [],
        };
      }

      const data = response.data;

      let normalized = {
        pendingInvites: [],
        historyInvites: [],
        upcomingEvents: [],
        activeEvents: [],
        pastEvents: data.pastEvents || [],
      };

      if (role === "usher") {
        const allEvents = [
          ...(data.upcomingEvents || []),
          ...(data.activeEvents || []),
        ];

        const pendingEvents = allEvents.filter((event) => {
          const usherCheckIn = event.checkIns?.find(
            (checkIn) => checkIn.usherId?.toString() === userId,
          );
          return !usherCheckIn || usherCheckIn.approvalStatus === "pending";
        });

        const acceptedEvents = allEvents.filter((event) => {
          const usherCheckIn = event.checkIns?.find(
            (checkIn) => checkIn.usherId?.toString() === userId,
          );
          return usherCheckIn && usherCheckIn.approvalStatus !== "pending";
        });

        normalized.pendingInvites = pendingEvents.map((event) => ({
          event,
          isPending: true,
          invitationStatus: "pending",
        }));

        normalized.historyInvites = acceptedEvents.map((event) => ({
          event,
          isPending: false,
          invitationStatus: "accepted",
        }));

        normalized.upcomingEvents = allEvents;
        normalized.activeEvents = data.activeEvents || [];
      } else {
        normalized.pendingInvites = Array.isArray(data.pendingInvites)
          ? data.pendingInvites
          : [];
        normalized.historyInvites = Array.isArray(data.historyInvites)
          ? data.historyInvites
          : [];
        normalized.upcomingEvents = data.upcomingEvents || [];
        normalized.activeEvents = data.activeEvents || [];
      }
      return normalized;
    } catch (error) {
      setError(
        error.response?.data?.message ||
          "Failed to load invites. Please check your connection and try again.",
      );
      return {
        pendingInvites: [],
        historyInvites: [],
        upcomingEvents: [],
        activeEvents: [],
        pastEvents: [],
      };
    }
  };

  const handleCloseBenefitsModal = () => {
    setShowRoleBenefitsModal(false);

    navigate(location.pathname, {
      replace: true,
      state: {},
    });
  };
  const sendInvitationResponse = async (responseResult, eventId) => {
    try {
      const response = await axios.put(`/event/${eventId}/invitation/respond`, {
        guestId: userId,
        status: responseResult,
      });

      return response.data.successStatus;
    } catch {
      return false;
    }
  };

  const updateInviteLocally = (eventId, status) => {
    setPendingInvites((prev) =>
      prev.filter((inv) => inv.event._id !== eventId),
    );

    setHistoryInvites((prev) => [
      ...prev,
      {
        event: pendingInvites.find((e) => e.event._id === eventId)?.event,
        invitationStatus: status,
      },
    ]);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleAccept = (event) => {
    setSelectedEvent(event);
    setIsAcceptModalOpen(true);
  };

  const handleDecline = (event) => {
    setSelectedEvent(event);
    setIsDeclineModalOpen(true);
  };

  const handleAcceptProceed = async () => {
    setIsAcceptModalOpen(false);

    updateInviteLocally(selectedEvent._id, "accepted");

    if (
      selectedEvent?.isEventAttireCompulsory ||
      selectedEvent?.isEntryFeeApplicable
    ) {
      setIsPurchaseOptionsModalOpen(true);
    } else {
      handleViewEvent(selectedEvent);
    }

    sendInvitationResponse("accepted", selectedEvent._id);
  };

  const handlePurchaseClick = (type, options) => {
    setPurchaseType(type);
    setPurchaseOptions(options);
    setIsPurchaseOptionsModalOpen(false);
    setIsMandatoryPurchaseModalOpen(true);
  };

  const handleMandatoryPurchaseProceed = (purchaseData) => {
    setIsMandatoryPurchaseModalOpen(false);
    setSelectedPurchase(purchaseData);
    handleViewEvent(selectedEvent);
  };

  const handleMandatoryPurchasePayLater = () => {
    setIsMandatoryPurchaseModalOpen(false);
    setIsPurchaseOptionsModalOpen(false);
    handleViewEvent(selectedEvent);
  };

  const handlePurchaseOptionsProceed = async () => {
    setIsPurchaseOptionsModalOpen(false);
    handleViewEvent(selectedEvent);
  };

  const handleDeclineProceed = async () => {
    setIsDeclineModalOpen(false);

    updateInviteLocally(selectedEvent._id, "declined");

    sendInvitationResponse("declined", selectedEvent._id);
    handleViewEvent(selectedEvent);
  };

  const handleViewEvent = (event) => {
    navigate(`/eventManagementDetails/${event.id || event._id}`, {
      state: {
        eventTitle: event.title || event.eventName || "Event",
        eventDate: event.eventDate || event.startDate,
        eventStatus: getEventStatus(event.eventDate || event.startDate),
        eventDetails: event,
        reloadHome: true,
        userRole: user?.role,
      },
    });
  };

  const handleAddEvent = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleProceed = async (code, newEventData) => {
    setIsModalOpen(false);

    setPendingInvites((prev) => [
      { event: newEventData, isPending: true },
      ...prev,
    ]);
    setEvents((prev) => ({
      upcomingEvents: [newEventData, ...(prev.upcomingEvents || [])],
      pastEvents: prev.pastEvents || [],
    }));

    setLoading(true);
    try {
      const data = await fetchInvites(userId, role);
      if (data) {
        setPendingInvites(data.pendingInvites || []);
        setHistoryInvites(data.historyInvites || []);
        setEvents({
          upcomingEvents: [
            ...(data.upcomingEvents || []),
            ...(data.activeEvents || []),
          ],
          pastEvents: data.pastEvents || [],
        });
      }
    } finally {
      setLoading(false);
      navigate("/invites?reload=true");
    }
  };

  const displayedEvents =
    activeTab === "upcoming"
      ? pendingInvites.map(({ event }) => ({ ...event, isPending: true }))
      : activeTab === "history"
        ? [
            ...historyInvites.map(({ event }) => event),
            ...(events.upcomingEvents?.filter((event) => {
              if (isUsher) {
                const usherCheckIn = event.checkIns?.find(
                  (checkIn) => checkIn.usherId?.toString() === userId,
                );
                return (
                  usherCheckIn && usherCheckIn.approvalStatus !== "pending"
                );
              }
              return event.invitations?.some(
                (inv) =>
                  inv.guestId.toString() === userId &&
                  ["accepted", "declined"].includes(inv.status),
              );
            }) ?? []),

            ...(events.activeEvents?.filter((event) => {
              if (isUsher) {
                const usherCheckIn = event.checkIns?.find(
                  (checkIn) => checkIn.usherId?.toString() === userId,
                );
                return (
                  usherCheckIn && usherCheckIn.approvalStatus !== "pending"
                );
              }
              return event.invitations?.some(
                (inv) =>
                  inv.guestId.toString() === userId &&
                  ["accepted", "declined"].includes(inv.status),
              );
            }) ?? []),

            ...(events.pastEvents?.filter((event) => {
              if (isUsher) {
                const usherCheckIn = event.checkIns?.find(
                  (checkIn) => checkIn.usherId?.toString() === userId,
                );
                return (
                  usherCheckIn && usherCheckIn.approvalStatus !== "pending"
                );
              }
              return event.invitations?.some(
                (inv) =>
                  inv.guestId.toString() === userId &&
                  ["accepted", "declined"].includes(inv.status),
              );
            }) ?? []),
          ].filter(
            (event, index, self) =>
              index === self.findIndex((e) => e._id === event._id),
          )
        : [];

  const isEventInPast = (eventDate) => {
    if (!eventDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(eventDate);
    if (isNaN(date.getTime())) return false;
    date.setHours(0, 0, 0, 0);
    return date < today;
  };

  const getEventStatusForCard = (event) => {
    if (isUsher) {
      const usherCheckIn = event.checkIns?.find(
        (checkIn) => checkIn.usherId?.toString() === userId,
      );

      if (!usherCheckIn) return "pending";

      return usherCheckIn.approvalStatus;
    } else {
      const userInvitation = event.invitations?.find(
        (inv) => inv.guestId.toString() === userId,
      );
      return userInvitation?.status || "pending";
    }
  };

  const getUsherStatusMessage = (event) => {
    if (!isUsher) return null;

    const usherCheckIn = event.checkIns?.find(
      (checkIn) => checkIn.usherId?.toString() === userId,
    );

    if (!usherCheckIn) return null;

    switch (usherCheckIn.approvalStatus) {
      case "confirmed":
        return "Host has approved your usher request";
      case "rejected":
        return "Host declined your usher request";
      case "pending":
        return "Your request is pending host approval";
      default:
        return null;
    }
  };

  return (
    <div className="invites-page">
      <Header />
      <div className="invites-dashboard-box">
        <div className="invites-header-content">
          <h2 className="invites-section-title">Invites</h2>
          <div className="invites-header-right">
            <div className="invites-date-card">
              <div className="invites-calendar-icon">
                <CalendarIcon />
              </div>
              <div className="invites-date-info">
                <p>Today's Date</p>
                <h3>{formatDateWithOrdinal(currentDate)}</h3>
              </div>
            </div>
            <button
              className="invites-join-event-button"
              // onClick={handleAddEvent}
              onClick={() => navigate("/joinEvent")}
            >
              <span className="invites-plus-icon">
                <PlusCircleIcon />
              </span>
              Join Event
            </button>
          </div>
        </div>
      </div>
      <div className="invites-section">
        <div className="invites-tabs-and-search">
          <div className="invites-tabs-container">
            <TabSelector
              tabs={[
                { id: "upcoming", label: "New Invites" },
                { id: "history", label: "History" },
              ]}
              onTabChange={handleTabChange}
              showActive={false}
              withSearch={true}
              badgeCount={displayedEvents ? displayedEvents.length : 0}
            />
          </div>
        </div>
        <div className="invites-list">
          {loading ? (
            <div>Loading invites...</div>
          ) : displayedEvents.length === 0 && activeTab === "upcoming" ? (
            <div className="invites-no-invites-message">
              <h2>No New Invites</h2>
              <p>You don't have any new invites at the moment.</p>
            </div>
          ) : displayedEvents.length === 0 ? (
            <div>
              No {activeTab === "upcoming" ? "new" : "past"} invites found.
            </div>
          ) : (
            displayedEvents.map((event) => {
              const status = getEventStatusForCard(event);
              const componentStatus =
                status === "accepted" || status === "confirmed"
                  ? "Accepted"
                  : status === "declined" || status === "rejected"
                    ? "Declined"
                    : status === "pending"
                      ? "Pending"
                      : "Pending";

              const isPastEvent = isEventInPast(
                event.eventDate || event.startDate,
              );

              const usherStatusMessage = getUsherStatusMessage(event);

              const showButtons =
                !isPastEvent &&
                (isUsher ? false : event.isPending || status === "pending");

              // Calculate check-in stats for ushers
              const checkedInCount = isUsher
                ? event.checkIns?.find(
                    (checkIn) => checkIn.usherId?.toString() === userId,
                  )?.checkedInGuests?.length || 0
                : 0;
              const totalGuests = event.invitations?.length || 0;

              return (
                <EventSmallCard
                  key={event._id}
                  eventTitle={event.eventTitle || "Untitled Event"}
                  eventType={event.eventType || "Unknown"}
                  date={formatDateLessThanOneDay(
                    event.eventDate ||
                      event.startDate ||
                      (event.multipleDates && event.multipleDates[0]?.date),
                  )}
                  time={
                    event.eventTime ||
                    event.startTime ||
                    new Date(
                      event.eventDate ||
                        event.startDate ||
                        event.nextOccurrence,
                    ).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  }
                  location={
                    event.eventLandmark ||
                    event.eventLocation ||
                    "Not specified"
                  }
                  image={
                    event.eventImages?.[0]?.uri || "/images/eventImage.jpg"
                  }
                  onPress={() => handleViewEvent(event)}
                  onDecline={() => handleDecline(event)}
                  onAccept={() => handleAccept(event)}
                  showButtons={showButtons}
                  showRSVPButton={!isPastEvent}
                  showAttendees={false}
                  showExternalLink={false}
                  status={componentStatus}
                  isUsher={isUsher}
                  checkedInCount={checkedInCount}
                  totalGuests={totalGuests}
                  usherStatusMessage={usherStatusMessage}
                  role={role}
                />
              );
            })
          )}
        </div>
        {showRoleBenefitsModal && newRole && (
          <div className="profile-img-modal-overlay">
            <div className="profile-img-modal-container">
              <h3>
                {newRole === "usher" ? "Usher Role Added" : "Guest Role Added"}
              </h3>

              <p style={{ textAlign: "left" }}>
                <br />
                The <strong>{newRole}</strong> role has been added successfully.
                <br />
                {newRole === "usher" ? (
                  <>
                    As an <strong>usher</strong>, you can:
                    <br />
                    <br />
                    <ul style={{ paddingLeft: "20px" }}>
                      <li>Manage guest check-ins</li>
                      <li>Assist hosts during events</li>
                      <li>Access usher dashboard tools</li>
                      <li>Support smooth event coordination</li>
                    </ul>
                  </>
                ) : (
                  <>
                    As a <strong>guest</strong>, you can:
                    <br />
                    <br />
                    <ul style={{ paddingLeft: "20px" }}>
                      <li>Join events via invite codes</li>
                      <li>Receive event updates</li>
                      <li>View event details anytime</li>
                      <li>Manage invitations easily</li>
                    </ul>
                  </>
                )}
              </p>

              <p style={{ textAlign: "left" }}>
                You can switch roles anytime using the
                <strong> Switch Account</strong> tab.
              </p>

              <button
                className="profile-img-primary-button"
                onClick={handleCloseBenefitsModal}
              >
                Continue
              </button>
            </div>
          </div>
        )}
      </div>

      <AddEventModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onProceed={handleProceed}
      />

      {!isUsher && (
        <>
          <AcceptModal
            isOpen={isAcceptModalOpen}
            onClose={() => setIsAcceptModalOpen(false)}
            onProceed={handleAcceptProceed}
            event={selectedEvent}
            role={role}
          />

          <PurchaseOptionsModal
            isOpen={isPurchaseOptionsModalOpen}
            onClose={() => setIsPurchaseOptionsModalOpen(false)}
            onProceed={handlePurchaseOptionsProceed}
            onPayLater={handleMandatoryPurchasePayLater}
            onPurchaseClick={handlePurchaseClick}
            event={selectedEvent}
            user={user}
          />

          <MandatoryPurchaseModal
            isOpen={isMandatoryPurchaseModalOpen}
            onClose={() => setIsMandatoryPurchaseModalOpen(false)}
            onProceed={handleMandatoryPurchaseProceed}
            onPayLater={handleMandatoryPurchasePayLater}
            event={selectedEvent}
            type={purchaseType}
            options={purchaseOptions}
          />

          <DeclineModal
            isOpen={isDeclineModalOpen}
            onClose={() => setIsDeclineModalOpen(false)}
            onProceed={handleDeclineProceed}
            event={selectedEvent}
          />
        </>
      )}
    </div>
  );
};

export default Invites;
